import type { ROLE_ID } from '@auth';
import type { ValueOf } from 'kafkajs';

import type { LoginResponse, PaginatinatedApiResponse, RefreshResponse } from '~/types';

import GatewayBase from './gateway-base';

/**
 * Which surfaces a Child Account's users may see (ENG-5850). Polarity is visibility: true = visible.
 * Navigation keys remove whole destinations; `showPolicy*` keys remove one card from Settings → Policies.
 */
export type AccountVisibilitySettings = {
  showFinance: boolean;
  showInvoices: boolean;
  showBillingSettings: boolean;
  showAccountDashboard: boolean;
  showChildAccountDashboard: boolean;
  showPolicies: boolean;
  showPolicyShippingPricing: boolean;
  showPolicyBilling: boolean;
  showPolicyInvoices: boolean;
  showPolicyPayouts: boolean;
  showPolicyCoverageMatrix: boolean;
  showPolicyWindows: boolean;
  showPolicyWidget: boolean;
};

/** A Child Account beneath a Parent Account, with its visibility settings inlined. */
export type ChildAccount = {
  id: string;
  name: string;
  nameSlug: string;
  createdAt: string;
  settings: AccountVisibilitySettings | null;
};

/** Whether a user's access spans their whole account or only the stores they hold a store role on. */
export enum AccountUserScopeType {
  AccountWide = 'account',
  StoreScoped = 'store',
}

/** One row of the Account Admin Users tab (ENG-5850): a user somewhere in the caller's tree. */
export type AccountTreeUser = {
  id: string;
  name: string | null;
  email: string;
  roleName: string;
  lastActive: string | null;
  accountId: string;
  accountName: string | null;
  scope: {
    type: AccountUserScopeType;
    storeCount: number;
  };
};
import type {
  ActiveImpersonationSessionResponse,
  Identity,
  ImpersonationCurrentSessionResponse,
  ImpersonationTargetSummary,
} from '@monolog/shared/types';
import {
  MFAMethod,
  VerifyMfaRequest,
  UpdateNotificationPreferencesInput,
  SendMfaEmailResponse,
} from '@monolog/shared/types';
import type { UserNotificationPreference, User as DbUser } from '@monolog/shared/types/database/identity-svc';

export type SeasonKey = 'halloween' | 'thanksgiving';

export type SeasonalThemeState = {
  // Engineering's switch that makes the season live for everyone; a season with nothing saved is off.
  enabled: boolean;
  preference: { eggs: Record<string, boolean>; enabled: boolean };
};

export type SeasonalThemesResponse = Record<SeasonKey, SeasonalThemeState>;

export type UserInfo = {
  email_verified: boolean;
  email: string;
  family_name: string;
  gender: string;
  given_name: string;
  name: string;
  phone_number_verified: boolean;
  phone_number: string;
  picture: string;
  profile: string;
  sub: string;
  updated_at: string;
  website: string;
  zoneinfo: string;
};

type ErrorResponse = {
  error: string;
  errorCode: string;
};

export type PersonalAccessTokenStatus = 'active' | 'expired' | 'revoked';

export type PersonalAccessToken = {
  id: string;
  name: string;
  tokenPreview: string;
  accountId: string;
  createdByUserId: string;
  createdByUser?: { id: string; firstName?: string; lastName?: string; fullName?: string; email?: string };
  storeIds: string[];
  scopes: string[];
  expiresAt: string | null;
  revokedAt: string | null;
  deletedAt: string | null;
  lastUsedAt: string | null;
  lastUsedStoreId: string | null;
  lastUsedIp: string | null;
  lastUsedUserAgent: string | null;
  createdAt: string;
  status: PersonalAccessTokenStatus;
};

export type CreatePersonalAccessTokenInput = {
  name: string;
  storeIds: string[];
  scopes: string[];
  expiresAt?: string | null;
};

// The raw token is returned exactly once, immediately after creation (PAT spec FR-6).
export type CreatedPersonalAccessToken = PersonalAccessToken & { token: string };

export default class IdentityService extends GatewayBase {
  get users() {
    return {
      me: this.me,
      mentions: this.getMentionedUsers,
    };
  }

  get profile() {
    return {
      update: this.updateProfile,
    };
  }

  get teamMembers() {
    return {
      add: this.createTeamMember,
      changeAccess: this.changeMemberAccess,
      changeRole: this.changeMemberRole,
      counts: this.getTeamMemberCounts,
      edit: this.editTeamMember,
      escalationResolver: { get: this.getEscalationResolver, set: this.setEscalationResolver },
      findOne: this.findOneUser,
      list: this.getTeamMembers,
    };
  }

  private getImpersonationSession = () => {
    return this.client.get<ImpersonationCurrentSessionResponse>('/v1/auth/impersonation/current', {
      withCredentials: true,
    });
  };

  private listImpersonationTargets = (search?: string) => {
    return this.client.get<ImpersonationTargetSummary[]>('/v1/auth/impersonation/targets', {
      params: search ? { search } : undefined,
      withCredentials: true,
    });
  };

  private startImpersonation = (targetUserId: string) => {
    return this.client.post<ActiveImpersonationSessionResponse>(
      '/v1/auth/impersonation/start',
      { target: { mode: 'User', targetUserId } },
      { withCredentials: true },
    );
  };

  private stopImpersonation = () => {
    return this.client.post<{ result: 'Absent' }>('/v1/auth/impersonation/stop', {}, { withCredentials: true });
  };

  get admin() {
    return {
      findOne: this.findOneUser,
      userCounts: this.getAdminUsersCounts,
      users: {
        activate: this.activateUser,
        create: this.createAdminUser,
        deactivate: this.deactivateUser,
        list: this.getAdminUsers,
        update: this.adminUpdateUsers,
      },
    };
  }

  get auth() {
    return {
      exchangeDri: this.exchangeDri,
      exchangeFluidSession: this.exchangeFluidSession,
      exchangeShopifyLogin: this.exchangeShopifyLogin,
      getMfaMethods: this.getMfaMethods,
      login: this.login,
      logout: this.logout,
      mfaVerify: this.mfaVerify,
      password: {
        change: this.changePassword,
        requestChange: this.requestChangePassword,
      },
      refresh: this.refreshToken,
      sendMfaEmail: this.sendMfaEmail,
    };
  }

  get impersonation() {
    return {
      current: this.getImpersonationSession,
      start: this.startImpersonation,
      stop: this.stopImpersonation,
      targets: this.listImpersonationTargets,
    };
  }

  get apiTokens() {
    return {
      create: this.createToken,
      delete: this.deleteToken,
      list: this.getTokens,
    };
  }

  /** User-owned personal access tokens (PAT spec). Distinct from legacy store-scoped `apiTokens`. */
  get personalAccessTokens() {
    return {
      create: this.createPat,
      delete: this.deletePat,
      listForAccount: this.listAccountPats,
      listMine: this.listMyPats,
      rename: this.renamePat,
      revoke: this.revokePat,
    };
  }

  get accounts() {
    return {
      children: this.listChildAccounts,
      childrenOf: this.listChildAccountsOf,
      create: this.createAccount,
      findOne: this.findOneAccount,
      list: this.listAccounts,
      remove: this.removeAccount,
      settings: {
        get: this.getAccountSettings,
        update: this.updateAccountSettings,
      },
      transferOwner: this.transferOwnership,
      update: this.updateAccount,
      users: this.listAccountTreeUsers,
    };
  }

  get seasonalThemes() {
    return {
      get: this.getSeasonalThemes,
      setGlobal: this.setSeasonalThemeGlobal,
      setPreference: this.setSeasonalThemePreference,
    };
  }

  get notificationPreferences() {
    return {
      get: this.getNotificationPreferences,
      update: this.updateNotificationPreferences,
    };
  }

  /** Org-side store collaboration (Shopify-style collaborator access) management. */
  get collaborations() {
    return {
      cancel: this.cancelCollaboration,
      list: this.listCollaborations,
      request: this.requestCollaboration,
    };
  }

  private requestCollaboration = (storeDomain: string) => {
    return this.client.post<{ id: string; storeId: string; code: string; status: string }>('/v1/store-collaborations', {
      storeDomain,
    });
  };

  private listCollaborations = () => {
    return this.client.get<
      Array<{
        id: string;
        storeId: string;
        storeAccountId: string | null;
        status: string;
        code: string;
        createdAt: string;
      }>
    >('/v1/store-collaborations');
  };

  private cancelCollaboration = (id: string) => {
    return this.client.delete(`/v1/store-collaborations/${id}`);
  };

  private listAccounts = () => {
    // `parentAccountId` already comes back on the row — the admin Stores table uses it for account
    // lineage and the Parent Account filter (ENG-5850 Surface D).
    return this.client.get<Array<{ id: string; name: string; parentAccountId: string | null }>>('/v1/accounts');
  };

  /**
   * The caller's child (brand) accounts, each with its visibility settings — empty unless the
   * caller is a reseller (ENG-5850). `settings` is null for a brand that has never been configured,
   * which reads as fully visible.
   */
  private listChildAccounts = () => {
    return this.client.get<ChildAccount[]>('/v1/accounts/children');
  };

  // Children of ANY account, for the OP-staff hand-off into a reseller's Account Admin area (ENG-5850 Track 2D).
  private listChildAccountsOf = (accountId: string) => {
    return this.client.get<ChildAccount[]>(`/v1/accounts/${accountId}/children`);
  };

  // Users across the caller's account tree (ENG-5850); OP staff must name the target tree via `accountId`.
  private listAccountTreeUsers = (accountId?: string) => {
    return this.client.get<AccountTreeUser[]>('/v1/accounts/users', {
      params: accountId ? { accountId } : undefined,
    });
  };

  private getAccountSettings = (id: string) => {
    return this.client.get<AccountVisibilitySettings | null>(`/v1/accounts/${id}/settings`);
  };

  /** Flip a child account's finance visibility. Refused unless the caller is its parent or OP. */
  private updateAccountSettings = (id: string, input: Partial<AccountVisibilitySettings>) => {
    return this.client.put<AccountVisibilitySettings>(`/v1/accounts/${id}/settings`, input);
  };

  private findOneAccount = (id: string) => {
    return this.client.get<{ id: string; name: string }>(`/v1/accounts/${id}`);
  };

  private createAccount = (input: {
    name: string;
    user: { firstName?: string; lastName?: string; email: string; password?: string };
  }) => {
    return this.client.post<{ id: string; name: string }>('/v1/accounts', input);
  };

  private updateAccount = (id: string, input: { name?: string }) => {
    return this.client.put<{ id: string; name: string }>(`/v1/accounts/${id}`, input);
  };

  private removeAccount = (id: string) => {
    return this.client.delete(`/v1/accounts/${id}`);
  };

  private transferOwnership = (userId: string) => {
    return this.client.post('/v1/accounts/transfer-owner', { userId });
  };

  private editTeamMember = (id: string, update: Partial<Identity.User> & { storeRoleId?: number }) => {
    return this.client.put<Identity.User>(`/v1/users/${id}`, update);
  };

  private createAdminUser = (input: { email: string; password: string; roleId: number }) => {
    return this.client.post<Identity.User>('/v1/admin/users', input);
  };

  private changePassword = (password: string, token: string) => {
    return this.client.post('/v1/auth/password/change', { password, token });
  };

  private requestChangePassword = (email: string) => {
    return this.client.post('/v1/auth/password/request', { email });
  };

  private createToken = (storeId: string, name: string) => {
    return this.client.post<{ id: string; name: string; token: string }>(`/v1/auth/${storeId}/api-tokens`, { name });
  };

  private deleteToken = (storeId: string, id: string) => {
    return this.client.delete(`/v1/auth/${storeId}/api-tokens/${id}`);
  };

  private getTokens = (storeId: string) => {
    return this.client.get<{ id: string; name: string }[]>(`/v1/auth/${storeId}/api-tokens`);
  };

  private createPat = (input: CreatePersonalAccessTokenInput) => {
    return this.client.post<CreatedPersonalAccessToken>('/v1/personal-access-tokens', input);
  };

  private listMyPats = () => {
    return this.client.get<PersonalAccessToken[]>('/v1/personal-access-tokens/mine');
  };

  private listAccountPats = () => {
    return this.client.get<PersonalAccessToken[]>('/v1/personal-access-tokens/account');
  };

  private renamePat = (id: string, name: string) => {
    return this.client.patch<PersonalAccessToken>(`/v1/personal-access-tokens/${id}`, { name });
  };

  private revokePat = (id: string) => {
    return this.client.post<PersonalAccessToken>(`/v1/personal-access-tokens/${id}/revoke`, {});
  };

  private deletePat = (id: string) => {
    return this.client.delete<{ deleted: boolean }>(`/v1/personal-access-tokens/${id}`);
  };

  private findOneUser = (id: string) => {
    return this.client.get<Identity.User & { loginCount: number }>(`/v1/users/${id}`);
  };

  private login = (email: string, password: string) => {
    return this.client.post<LoginResponse & Partial<ErrorResponse>>('/v1/auth/login', { email, password });
  };

  private mfaVerify = (data: VerifyMfaRequest) => {
    return this.client.post<LoginResponse & Partial<ErrorResponse>>('/v1/auth/mfa/verify', data);
  };

  private refreshToken = (refreshToken: string) => {
    return this.client.post<RefreshResponse & Partial<ErrorResponse>>('/v1/auth/refresh', { refreshToken });
  };

  private logout = (refreshToken: string) => {
    return this.client.post<boolean>('/v1/auth/logout', { refreshToken });
  };

  private exchangeDri = (driToken: string) => {
    return this.client.post<LoginResponse>('/v1/fluid/dri/exchange', { driToken });
  };

  private exchangeFluidSession = (sessionToken: string, dri: string) => {
    return this.client.post<LoginResponse>('/v1/fluid/session/exchange', { dri, sessionToken });
  };

  private exchangeShopifyLogin = (shopifyLoginToken: string) => {
    return this.client.post<LoginResponse | { requiresLogin: true; shopifySession: string; storeId: string }>(
      '/v1/auth/shopify-login/exchange',
      { shopifyLoginToken },
    );
  };

  private getAdminUsersCounts = () => {
    return this.client.get<{ active: number; inactive: number }>('/v1/admin/users/counts');
  };

  private getTeamMemberCounts = (filter: { accountId?: string; storeId?: string }) => {
    return this.client.get<{ active: number; inactive: number }>('/v1/users/counts', {
      params: {
        filter,
      },
    });
  };

  private adminUpdateUsers = (id: string, update: Partial<Identity.User>) => {
    return this.client.put<Identity.User>(`/v1/admin/users/${id}`, update);
  };

  private updateProfile = (update: { picture?: string }) => {
    return this.client.put('/v1/auth/profile', update);
  };

  private createTeamMember = (input: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    roleId: number;
    storeRoleId?: number;
    storeId?: string;
    storeIds?: string[];
  }) => {
    return this.client.post<Identity.User>('/v1/users', input);
  };

  // Change a member's account-level role (POST /v1/users/:id/role).
  private changeMemberRole = (id: string, roleId: number) => {
    return this.client.post<Identity.User>(`/v1/users/${id}/role`, { roleId });
  };

  // Move a member to another account in the tree with an account role (PATCH /v1/users/:id/access).
  private changeMemberAccess = (id: string, accountId: string, roleId: number) => {
    return this.client.patch<Identity.User>(`/v1/users/${id}/access`, { accountId, roleId });
  };

  // Read whether a member holds the Escalation Resolver add-on (GET /v1/users/:id/escalation-resolver).
  private getEscalationResolver = (id: string) => {
    return this.client.get<{ enabled: boolean; canManage: boolean }>(`/v1/users/${id}/escalation-resolver`);
  };

  // Grant/revoke the Escalation Resolver add-on (PATCH /v1/users/:id/escalation-resolver).
  private setEscalationResolver = (id: string, enabled: boolean) => {
    return this.client.patch<{ enabled: boolean }>(`/v1/users/${id}/escalation-resolver`, { enabled });
  };

  private getTeamMembers = (params: {
    accountId?: string;
    storeId?: string;
    page?: number;
    size?: number;
    status?: 'active' | 'inactive';
    ids?: string[];
    roleId?: number[];
  }) => {
    return this.client.get<{
      users: Identity.User[];
      totalCount: number;
    }>('/v1/users', { params });
  };

  private getAdminUsers = (params: any) => {
    return this.client.get<{
      users: Identity.User[];
      totalCount: number;
    }>('/v1/admin/users', { params });
  };

  private deactivateUser = (id: string) => {
    return this.client.put(`/v1/admin/users/${id}/deactivate`);
  };

  private activateUser = (id: string) => {
    return this.client.put(`/v1/admin/users/${id}/activate`);
  };

  private me = () => {
    return this.client.get<UserInfo>('/v1/auth/userinfo');
  };

  private getMentionedUsers = (searchText: string, storeId?: string) => {
    return this.client.get<Identity.User[]>(`/v1/users/mentions`, { params: { searchText, storeId } });
  };

  private getSeasonalThemes = () => {
    return this.client.get<SeasonalThemesResponse>('/v1/seasonal-themes', undefined, { noCache: true });
  };

  private setSeasonalThemeGlobal = (season: SeasonKey, enabled: boolean) => {
    return this.client.put(`/v1/seasonal-themes/${season}`, { enabled });
  };

  private setSeasonalThemePreference = (
    season: SeasonKey,
    input: { eggs?: Record<string, boolean>; enabled?: boolean },
  ) => {
    return this.client.put(`/v1/seasonal-themes/${season}/preference`, input);
  };

  private getNotificationPreferences = () => {
    return this.client.get<UserNotificationPreference | null>('/v1/users/notification-preferences');
  };

  private updateNotificationPreferences = (input: UpdateNotificationPreferencesInput) => {
    return this.client.put('/v1/users/notification-preferences', input);
  };

  private getMfaMethods = () => {
    return this.client.get<MFAMethod[]>(`/v1/auth/mfa/methods`);
  };

  private sendMfaEmail = (mfaToken: string) => {
    return this.client.post<SendMfaEmailResponse>(`/v1/auth/mfa/email`, { mfaToken });
  };
}
